import React, { FunctionComponent, useState, useEffect, useCallback } from "react"
import { useApolloClient, useLazyQuery } from "@apollo/client"
import { Button } from "@material-ui/core"
import PublishIcon from "@material-ui/icons/Publish"
import { PromptsTable } from "./PromptsTable"
import FilesPageStyles from "./FilesPage.module.scss"
import LoadingSpinnerStyles from "../../common/LoadingSpinner.module.scss"
import { Prompt, PromptPage, PromptSortFields } from "../../../typings/types"
import { CircularProgress, Grid, Paper, Typography } from "@material-ui/core"
import { SearchInput } from "../../components/SearchInput"
import { DownloadHelpers, File } from "../../common/DownloadHelpers"
import { useNotification } from "../../common/hooks/useNotification"
import SEARCH_PROMPTS from "../../queries/SEARCH_PROMPTS"
import { useQueryParams } from "../../common/hooks/useQueryParams"
import GET_PROMPT_AUDIO_DATA from "../../queries/GET_PROMPT_AUDIO_DATA"
import { Title } from "../../components/Title"
import { PromptsTableProvider } from "./contexts/PromptsTableContext"
import { PromptsTablePaginate } from "./PromptsTablePaginate"
import { UploadFormModal } from "./upload-form/UploadFormModal"

export const FilesPage: FunctionComponent = props => {
    const [prompts, setPrompts] = useState<Prompt[]>([])
    const [totalPages, setTotalPages] = useState<number>(0)
    const [currentPage, setCurrentPage] = useState(1)
    const [searchInput, setSearchInput] = useState("")
    const [activeSortFields, setActiveSort] = useState<PromptSortFields>({})
    const [loadingBulkDownload, setLoadingBulkDownload] = useState(false)
    const [uploadFormOpen, setUploadFormOpen] = useState(false)
    const { notifyError } = useNotification()

    const PROMPTS_PER_PAGE = 10

    const queryParams = useQueryParams()

    const [
        searchPrompts,
        { data: searchData, loading: searchLoading, error: searchError, refetch: refetchPrompts }
    ] = useLazyQuery<{
        searchPrompts: PromptPage
    }>(SEARCH_PROMPTS, {
        variables: {
            searchInput: searchInput,
            pageSize: PROMPTS_PER_PAGE,
            currentPage: currentPage,
            sort: activeSortFields
        },
        fetchPolicy: "no-cache"
    })

    const client = useApolloClient()

    const downloadFiles = useCallback(
        (uuids: string[]) => {
            setLoadingBulkDownload(true)
            const queries = uuids.map(uuid => {
                return client.query({
                    variables: {
                        mediaUUID: uuid
                    },
                    query: GET_PROMPT_AUDIO_DATA
                })
            })

            Promise.all(queries)
                .then(results => {
                    const files = results.map(result => {
                        const languagesCount = result?.data?.getPrompt?.languages?.length
                        let folderName
                        if (!languagesCount) folderName = "audio"
                        else if (languagesCount > 1) folderName = "multilingual"
                        else folderName = result?.data?.getPrompt?.languages[0]?.name

                        const promptName = result?.data?.getPrompt?.downloadableAudio?.fileName
                        const binaryData = result?.data.getPrompt?.downloadableAudio?.binaryData

                        return {
                            folderName: folderName,
                            fileName: promptName,
                            binaryData
                        } as File
                    })
                    return DownloadHelpers.zipAndSave(files)
                })
                .catch(errors => {
                    notifyError("Error downloading selected files")
                })
                .finally(() => {
                    setLoadingBulkDownload(false)
                })
        },
        [client, setLoadingBulkDownload, prompts]
    )

    useEffect(() => {
        if (searchData?.searchPrompts) updatePrompts(searchData?.searchPrompts)
    }, [searchData])

    const refreshPrompts = () => {
        if (refetchPrompts) {
            refetchPrompts().then(({ data }) => {
                if (data) {
                    updatePrompts(data.searchPrompts)
                }
            })
        }
    }

    useEffect(() => {
        const transaction = queryParams.get("transaction")
        if (transaction) {
            setSearchInput(transaction)
        }
    }, [])

    function updatePrompts(newPage: PromptPage) {
        setTotalPages(newPage.totalPages)
        setPrompts(newPage.prompts)
    }

    useEffect(() => {
        searchPrompts()
    }, [searchPrompts])

    function handleCloseUploadForm() {
        setUploadFormOpen(false)
        refreshPrompts()
    }

    return (
        <>
            <Title text={"Files"} />
            <div className={FilesPageStyles.FilesPage}>
                <div className={FilesPageStyles.FilesPage__table}>
                    <Grid container direction="row" spacing={3} alignItems="center">
                        <Grid item>
                            <Typography
                                variant="h6"
                                id="tableTitle"
                                component="div"
                                data-testid="toolbarTitle"
                            >
                                Files
                            </Typography>
                        </Grid>
                        <Grid item style={{ flex: 1 }}>
                            <SearchInput callback={(input): void => setSearchInput(input)} />
                        </Grid>
                        <Grid item>
                            <Button
                                id="open-upload-form-btn"
                                variant="contained"
                                onClick={() => setUploadFormOpen(true)}
                                startIcon={<PublishIcon />}
                            >
                                Upload
                            </Button>
                        </Grid>
                    </Grid>
                    {(searchLoading || loadingBulkDownload) && (
                        <CircularProgress
                            className={LoadingSpinnerStyles.LoadingSpinner__absolute__center}
                            data-testid="promptsLoading"
                        />
                    )}
                    {searchError && (
                        <label data-testid="promptsError">{`Error! ${searchError}`}</label>
                    )}
                    {!searchError && (
                        <Paper className={FilesPageStyles.FilesPage__content}>
                            <PromptsTableProvider>
                                <PromptsTable
                                    prompts={prompts}
                                    refreshPrompts={refreshPrompts}
                                    setActiveSort={setActiveSort}
                                    onBulkDownload={downloadFiles}
                                />
                                <div className={FilesPageStyles.FilesPage__pagination}>
                                    <PromptsTablePaginate
                                        totalPages={totalPages}
                                        currentPage={currentPage}
                                        callback={setCurrentPage}
                                    />
                                </div>
                            </PromptsTableProvider>
                        </Paper>
                    )}
                </div>
            </div>
            <UploadFormModal open={uploadFormOpen} onClose={handleCloseUploadForm} />
        </>
    )
}
